import React from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, addDoc, doc, updateDoc, getDocs, query, where, arrayUnion,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { gradeFields } from "../shared/gradeModel";
import { createChecklist } from "../shared/checklistData";
import useFetchClasses from "../hooks/useTeacherClasses";
import BulkImport from "./BulkImport";
import AddStudentsHelp, { useAddStudentsHelp } from "./AddStudentsHelp";
import "./AddStudent.css";

// Teacher-facing roster import: paste/upload every student with a Class column and
// it creates any classes that don't exist (all owned by you) + enrolls each student
// into their class. Mirrors the admin directory import, minus the assign-to-other-
// teachers part (rules only let a teacher create classes they own).
export default function ImportClasses() {
  const { uid, schoolId } = useUser();
  const { classList } = useFetchClasses();
  const navigate = useNavigate();
  const { showHelp, openHelp, dismissHelp } = useAddStudentsHelp(uid);

  // Only your own active classes seed the "already exists" matching.
  const myClasses = (classList || []).filter(
    (c) => !c.isArchived && c.teacherId === uid
  );

  const handleImport = async ({ students, newClasses = [] }) => {
    if (!students?.length || !uid || !schoolId) return 0;
    const currentYear = new Date().getFullYear();

    // class name (lower) → id, seeded with your existing classes.
    const nameToId = {};
    myClasses.forEach((c) => { nameToId[(c.className || "").trim().toLowerCase()] = c.id; });

    // If you don't already have a homeroom, the first class we create becomes it
    // (a sensible default — change it anytime via the homeroom checkbox on Classes).
    const hasHomeroom = myClasses.some((c) => c.isHomeroom);

    // Create the new classes — all owned by you.
    let i = 0;
    for (const nc of newClasses) {
      const ref = await addDoc(collection(firestore, "classes"), {
        className: nc.name,
        teacherId: uid,
        schoolId,
        gradeLevel: nc.gradeLevel || "",
        schoolYear: `${currentYear}-${currentYear + 1}`,
        enrolledStudents: [],
        ...(!hasHomeroom && i === 0 ? { isHomeroom: true } : {}),
      });
      nameToId[nc.name.trim().toLowerCase()] = ref.id;
      // Seed one default checklist per imported class (renameable/deletable after).
      createChecklist({ ownerUid: uid, schoolId, classId: ref.id, title: "Checklist" })
        .catch((e) => console.error("Error seeding default checklist:", e));
      i++;
    }

    // Dedupe against the existing school roster by studentId.
    const rosterSnap = await getDocs(
      query(collection(firestore, "students"), where("schoolId", "==", schoolId))
    );
    const byId = {};
    rosterSnap.forEach((d) => {
      const data = d.data();
      if (data.studentId) byId[data.studentId] = { docId: d.id, ...data };
    });

    let count = 0;
    for (const s of students) {
      const classId = s.homeroomKey ? nameToId[s.homeroomKey] : null;
      const existing = byId[s.id];
      if (existing) {
        const update = { teachers: arrayUnion(uid) };
        if (classId) update.enrolledClasses = arrayUnion(classId);
        await updateDoc(doc(firestore, "students", existing.docId), update);
      } else {
        await addDoc(collection(firestore, "students"), {
          firstName: s.firstName,
          lastName: s.lastName,
          studentName: s.studentName,
          studentId: s.id,
          ...gradeFields(s.grade),
          schoolId,
          shared: true,
          addedBy: uid,
          teachers: [uid],
          enrolledClasses: classId ? [classId] : [],
        });
      }
      count++;
    }
    return count;
  };

  return (
    <div className="add-student-form-container">
      {showHelp && <AddStudentsHelp onClose={dismissHelp} />}

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Import Classes &amp; Students</h1>
        <button type="button" className="ash-help-link" onClick={openHelp}>
          How do I add students?
        </button>
      </div>
      <p style={{ color: "var(--color-secondary)", maxWidth: 620, margin: "0 0 20px" }}>
        Paste or upload one row per student with a <strong>Class</strong> column. Any class
        that doesn't exist yet is created and assigned to you, and each student is enrolled
        in their class. Students are added to the school directory.
      </p>

      <BulkImport
        mode="directory"
        assignToSelf
        classOptions={myClasses}
        defaultTeacherId={uid}
        onImport={handleImport}
      />

      <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => navigate("/displayclasses")}>Done — Back to Classes</button>
      </div>
    </div>
  );
}
